<script setup lang="ts">
import { watch, nextTick, computed } from "vue";
import { Toast } from "bootstrap";
import { useAlertStore, type ToastMessage } from "@/stores/alerts-store";

const alertStore = useAlertStore();

const toastConfig = {
  error: {
    iconClass: "bi-exclamation-square-fill",
    iconColor: "text-danger",
    delay: 8000,
  },
  warning: {
    iconClass: "bi-exclamation-triangle-fill",
    iconColor: "text-warning",
    delay: 6000,
  },
  info: {
    iconClass: "bi-info-square-fill",
    iconColor: "text-info",
    delay: 4000,
  },
  success: {
    iconClass: "bi-check-circle-fill",
    iconColor: "text-success",
    delay: 3000,
  },
};

function formatMessage(message: string): string {
  return message.replace(/\n/g, "<br><br>");
}

function getIconClass(type: string): string {
  return (
    toastConfig[type as keyof typeof toastConfig]?.iconClass || "bi-info-circle"
  );
}

function getIconColor(type: string): string {
  return (
    toastConfig[type as keyof typeof toastConfig]?.iconColor || "text-secondary"
  );
}

function getDelay(type: string): number {
  return toastConfig[type as keyof typeof toastConfig]?.delay || 5000;
}

// Watch for new toasts and automatically show them
watch(
  () => alertStore.toasts.length,
  async (newLength, oldLength) => {
    if (newLength > oldLength) {
      // New toast added, show the latest one
      await nextTick(); // Ensure DOM is updated

      const latestToast = alertStore.toasts[alertStore.toasts.length - 1];
      const toastElement = document.getElementById(latestToast.id);

      if (toastElement) {
        const toast = new Toast(toastElement, {
          autohide: true,
          delay: getDelay(latestToast.type),
        });

        toast.show();

        // Remove from store when toast is hidden (either auto or manual)
        toastElement.addEventListener(
          "hidden.bs.toast",
          () => {
            alertStore.removeToast(latestToast.id);
          },
          { once: true }
        );
      }
    }
  }
);
</script>

<template>
  <div aria-live="polite" aria-atomic="true" class="position-relative">
    <div
      class="toast-container position-fixed bottom-0 end-0 p-3"
      style="z-index: 11"
    >
      <div
        v-for="toast in alertStore.toasts"
        :key="toast.id"
        :id="toast.id"
        class="toast fade opacity-90 rounded-0 shadow-sm"
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
      >
        <div class="toast-header">
          <i
            :class="`${getIconClass(toast.type)} ${getIconColor(
              toast.type
            )} me-2 fs-5 rounded-0`"
          ></i>
          <strong class="me-auto">{{ toast.title }}</strong>
          <small class="text-muted">{{
            toast.timestamp.toLocaleTimeString()
          }}</small>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="toast"
            aria-label="Close"
          ></button>
        </div>

        <div class="toast-body" v-html="formatMessage(toast.message)"></div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss"></style>
